import { useRichText } from "@hooks/useRichText";
import {
  ComponentProps,
  FromSanityGraphQL,
  ResolvedLink,
  WithGatsbyImage,
} from "@ts/components";
import {
  CarouselSection,
  CustomImage,
  FeaturedCollection,
  SanityKeyed,
  SectionFeaturedCollection,
} from "@ts/sanity";
import React, { FC, useCallback, useEffect, useState } from "react";

export type FeaturedCollectionFromQuery = FeaturedCollection & {
  collectionContents: Array<SanityKeyed<CarouselSection>>;
};

type TabTitleProps = {
  title?: string;
};

export type FeaturedCollectionContent = SanityKeyed<
  WithGatsbyImage<
    {
      image: SanityKeyed<CustomImage>;
      link: ResolvedLink;
    },
    "image"
  >
>;

type FeaturedCollectionInputProps = ComponentProps &
  FromSanityGraphQL<SectionFeaturedCollection> & {
    _key: string;
    featuredCollectionContents: FeaturedCollectionFromQuery[];
  };

type FeaturedCollectionOutputProps = Omit<
  FeaturedCollectionInputProps,
  "collectionContents"
> & {
  contents: FeaturedCollectionContent[];
  tabTitle?: TabTitleProps[];
  handleTabClick: (title?: string) => void;
  activeTab?: string;
};

export type FeaturedCollectionCardProps = Omit<
  FeaturedCollectionContent,
  "_key"
>;

export const withFeaturedCollection =
  (Component: FC<FeaturedCollectionOutputProps>) =>
  ({
    name = "FeaturedCollection",
    analytics,
    featuredCollections,
    featuredCollectionContents,
    heading,
    location,
    _key,
    ...rest
  }: FeaturedCollectionInputProps) => {
    const { parseContent } = useRichText();
    const normalisedHeading = parseContent(heading);
    const normalisedFeaturedCollections =
      featuredCollections ?? featuredCollectionContents;

    const [tabTitle, setTabTitle] = useState<TabTitleProps[]>();
    const [activeTab, setActiveTab] = useState<string | undefined>("");

    const handleTabClick = useCallback((title?: string) => {
      setActiveTab(title);
    }, []);

    useEffect(() => {
      if (normalisedFeaturedCollections?.length > 1) {
        const title = normalisedFeaturedCollections?.map((content) => ({
          title: content.title,
        }));
        setTabTitle(title);
        setActiveTab(title?.[0]?.title);
      }
    }, [normalisedFeaturedCollections]);

    Component.displayName = name;
    return (
      <Component
        _key={_key}
        activeTab={activeTab}
        featuredCollectionContents={normalisedFeaturedCollections}
        handleTabClick={handleTabClick}
        heading={normalisedHeading}
        tabTitle={tabTitle}
        {...rest}
      />
    );
  };
